import Api from "@/api/api";
import {CoachContext, ModalContext} from "@/contexts";
import {Form, Modal} from "@/shared";
import {ChangeEvent, useContext, useState} from "react";
import {toast} from "react-toastify";
import "./CoachAddAthleteModal.scss";

type Props = {
  coachGroupId: string;
};

type FormErrors = {
  email?: string;
  name?: string;
  weight?: string;
  height?: string;
  gender?: string;
};

type FormValueType = {
  email?: string;
  name: string;
  weight: string;
  height: string;
  gender: string;
};

export function CoachAddAthleteModal({coachGroupId}: Props) {
  const {closeModal} = useContext(ModalContext);
  const {getTeams} = useContext(CoachContext);

  const [formError, setFormError] = useState<FormErrors>({});

  const [formValues, setFormValues] = useState<FormValueType>({
    email: "",
    name: "",
    weight: "",
    height: "",
    gender: "",
  });

  const [loading, setLoading] = useState(false);

  const {email, name, weight, height, gender} = formValues;

  const validate = () => {
    const errors: FormErrors = {};
    const requiredFields: Partial<keyof FormErrors>[] = [
      "name",
      "weight",
      "height",
      "gender",
    ];
    requiredFields.forEach((field) => {
      if (!formValues[field]) {
        errors[field] = "Required field";
      }
    });

    const decimalRegex = /^[0-9]*\.?[0-9]{1,1}$/;

    if (Object.keys(errors).length) {
      return errors;
    }

    if (email && !/^[A-Z0-9._%+-]+@[A-Z0-9-]+.+.[A-Z]{2,4}$/i.test(email)) {
      errors.email = "Invalid email address";
      return errors;
    }

    if (weight && !decimalRegex.test(weight.toString())) {
      errors.weight = "Invalid number";
      toast.error(
        "Weight should be a decimal number. Use only numbers and one dot. Shouldn't end with a dot. One number after the dot."
      );
      return errors;
    }

    if (height && !decimalRegex.test(height.toString())) {
      errors.height = "Invalid number";
      toast.error(
        "Height should be a decimal number. Use only numbers and one dot. Shouldn't end with a dot. One number after the dot."
      );
      return errors;
    }
  };

  const handleFormUpdate = (event: ChangeEvent<HTMLInputElement>) => {
    const {value, name} = event.target;

    setFormValues({...formValues, [name]: value});
  };

  const handleDropdownChange = (name: string, value: string) => {
    setFormValues({...formValues, [name]: value});
  };

  const addCoach = async () => {
    const errors = validate();
    if (errors) {
      setFormError(errors);
      return;
    }

    try {
      setLoading(true);

      const res = await Api.post(
        `/v2/coach/organizations/teams/${coachGroupId}/athletes`,
        formValues
      );
      if (res.status === 200) {
        await getTeams().then(() => closeModal());
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Some error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className="coach-add-athlete">
      <Modal.Header text="Add Athlete"/>
      <Form className="coach-add-athlete__form">
        <Form.Row>
          <Form.Label>Name</Form.Label>
          <Form.Input
            value={name}
            onChange={handleFormUpdate}
            errorMessage={formError.name}
            placeholder="Enter name"
            name="name"
          />
        </Form.Row>
        <Form.Row>
          <Form.Label>Email</Form.Label>
          <Form.Input
            value={email}
            errorMessage={formError.email}
            onChange={handleFormUpdate}
            name="email"
            placeholder="example@gmail.com"
          />
        </Form.Row>
        <Form.Row>
          <Form.Label>Weight (lbs)</Form.Label>
          <Form.Input
            value={weight}
            type="number"
            onChange={handleFormUpdate}
            errorMessage={formError.weight}
            placeholder="0.0"
            name="weight"
          />
        </Form.Row>
        <Form.Row>
          <Form.Label>Height (inches)</Form.Label>
          <Form.Input
            value={height}
            type="number"
            onChange={handleFormUpdate}
            errorMessage={formError.height}
            placeholder="0.0"
            name="height"
          />
        </Form.Row>
        <Form.Row>
          <Form.Label>Gender</Form.Label>
          <Form.Select
            value={gender}
            onChange={(gender) =>
              handleDropdownChange("gender", gender.toString())
            }
            options={[
              {text: "Male", value: "Male"},
              {text: "Female", value: "Female"},
            ]}
            errorMessage={formError.gender}
          />
        </Form.Row>
      </Form>
      <Modal.Footer>
        <Form.Button type="outline" onClick={closeModal}>
          CANCEL
        </Form.Button>

        <Form.Button loading={loading} onClick={addCoach}>
          ADD
        </Form.Button>
      </Modal.Footer>
    </Modal>
  );
}
